import type { Message, RealtimeChannel } from "ably";
import { useEffect, useRef } from "react";
import type { LastMessagePollingConfig } from "../core";
import { getRuntimeConfig } from "../core";
import type { DirectHistoryFetcher } from "../vision/directHistory";

export function useLastMessage(
	channel: RealtimeChannel | null,
	onLastMessage: (msg: Message) => void,
	pollingConfig?: LastMessagePollingConfig | null,
	directFetcher?: DirectHistoryFetcher | null,
) {
	const isFetchingRef = useRef(false);

	useEffect(() => {
		if (!channel && !directFetcher) return;
		const logger = getRuntimeConfig().logger;

		const fetchLastMessage = async () => {
			if (isFetchingRef.current) return;
			isFetchingRef.current = true;
			try {
				if (directFetcher) {
					const message = await directFetcher();
					if (!message) return;
					onLastMessage(message as unknown as Message);
					return;
				}

				if (!channel) return;
				const result = await channel.history({ limit: 1, direction: "backwards" });
				const lastMsg = result?.items?.[0];
				if (!lastMsg) return;

				onLastMessage(lastMsg);
			} catch (err) {
				logger.warning("INFO", "Error reading Ably history", err);
			} finally {
				isFetchingRef.current = false;
			}
		};

		fetchLastMessage();
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				fetchLastMessage();
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		let timeoutId: ReturnType<typeof setTimeout> | null = null;
		let intervalId: ReturnType<typeof setInterval> | null = null;

		const initialFetchDelayMs = pollingConfig?.initialFetchDelayMs;
		const pollIntervalMs = pollingConfig?.pollIntervalMs;
		const shouldPoll = initialFetchDelayMs && initialFetchDelayMs > 0 && pollIntervalMs && pollIntervalMs > 0;
		if (shouldPoll) {
			timeoutId = setTimeout(() => {
				fetchLastMessage();
				intervalId = setInterval(fetchLastMessage, pollIntervalMs);
			}, initialFetchDelayMs);
		}

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			if (timeoutId) clearTimeout(timeoutId);
			if (intervalId) clearInterval(intervalId);
		};
	}, [channel, onLastMessage, pollingConfig, directFetcher]);
}
